
import React from "react";
import { motion } from "framer-motion";
import {
  FiArrowUpRight,
  FiCheck,
  FiShield,
  FiMaximize2,
  FiLayers,
} from "react-icons/fi";

import PageTransition from "./Pagetransition";

/* ───────────────── DATA ───────────────── */

const PRODUCTS = [
  {
    num: "01",
    title: "Ventanas Tipo Modena",
    desc: "Perfiles premium con aislación superior, diseño elegante y máxima durabilidad para viviendas modernas.",
    badge: "Más elegida",
    icon: <FiLayers />,
  },
  {
    num: "02",
    title: "Puertas de Aluminio",
    desc: "Seguridad, minimalismo y terminaciones impecables para interiores y exteriores.",
    badge: "Premium",
    icon: <FiShield />,
  },
  {
    num: "03",
    title: "Aberturas Corredizas",
    desc: "Apertura suave y silenciosa para lograr espacios amplios y luminosos.",
    badge: "Moderno",
    icon: <FiArrowUpRight />,
  },
  {
    num: "04",
    title: "Paños Fijos",
    desc: "Diseñados para maximizar la entrada de luz natural y la amplitud visual.",
    badge: "Luminosidad",
    icon: <FiMaximize2 />,
  },
  {
    num: "05",
    title: "Combinados a Medida",
    desc: "Creamos soluciones personalizadas adaptadas a cada proyecto y estilo.",
    badge: "Personalizado",
    icon: <FiLayers />,
  },
  {
    num: "06",
    title: "Instalación Profesional",
    desc: "Montaje preciso y terminaciones premium realizadas por especialistas.",
    badge: "Garantía",
    icon: <FiCheck />,
  },
];

/* ───────────────── ANIMATIONS ───────────────── */

const container = {
  hidden: {},
  show: {
    transition: {
      staggerChildren: 0.12,
      delayChildren: 0.15,
    },
  },
};

const fadeUp = {
  hidden: {
    opacity: 0,
    y: 40,
  },
  show: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.9,
      ease: [0.22, 1, 0.36, 1],
    },
  },
};

/* ───────────────── CARD ───────────────── */

function ProductCard({ product }) {
  return (
    <motion.div
      variants={fadeUp}
      whileHover={{
        y: -10,
      }}
      transition={{
        duration: 0.45,
        ease: [0.22, 1, 0.36, 1],
      }}
      className="group relative overflow-hidden border border-[#c9973a]/10 bg-white/[0.03] backdrop-blur-xl"
    >
      {/* Glow */}
      <motion.div
        initial={{
          opacity: 0,
        }}
        whileHover={{
          opacity: 1,
        }}
        transition={{
          duration: 0.5,
        }}
        className="absolute inset-0 bg-gradient-to-br from-[#c9973a]/10 via-transparent to-[#8b3520]/20"
      />

      {/* Top line */}
      <motion.div
        initial={{
          scaleX: 0,
        }}
        whileHover={{
          scaleX: 1,
        }}
        transition={{
          duration: 0.45,
        }}
        className="absolute left-0 top-0 h-[1px] w-full origin-left bg-gradient-to-r from-[#c9973a] via-[#f0c96b] to-transparent"
      />

      {/* Number */}
      <div className="absolute right-6 top-4 text-[5rem] font-bold leading-none text-white/[0.04]">
        {product.num}
      </div>

      <div className="relative z-10 flex h-full flex-col p-7 md:p-8">
        {/* Badge */}
        <div className="mb-7 flex items-center justify-between">
          <div className="flex h-16 w-16 items-center justify-center rounded-none border border-[#c9973a]/20 bg-gradient-to-br from-[#c9973a]/20 to-[#8b3520]/20 text-3xl text-[#f0c96b]">
            {product.icon}
          </div>

          <span className="border border-[#c9973a]/20 px-3 py-1 text-[10px] font-medium uppercase tracking-[0.28em] text-[#c9973a]">
            {product.badge}
          </span>
        </div>

        {/* Content */}
        <div className="flex-1">
          <h3
            className="
              text-[2rem]
              leading-none
              text-[#f9f5ef]
              md:text-[2.3rem]
            "
            style={{
              fontFamily: "'Cormorant Garamond', serif",
              fontWeight: 500,
            }}
          >
            {product.title}
          </h3>

          <div className="mt-5 h-px w-16 bg-gradient-to-r from-[#c9973a] to-transparent" />

          <p
            className="
              mt-6
              text-[0.98rem]
              leading-8
              text-[#b7aea2]
            "
            style={{
              fontFamily: "'Jost', sans-serif",
              fontWeight: 300,
            }}
          >
            {product.desc}
          </p>
        </div>

        {/* Bottom */}
        <div className="mt-10 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-[7px] w-[7px] rounded-full bg-[#c9973a] shadow-[0_0_18px_rgba(201,151,58,0.9)]" />

            <span
              className="text-[0.72rem] uppercase tracking-[0.3em] text-[#8f8678]"
              style={{
                fontFamily: "'Jost', sans-serif",
              }}
            >
              Disponible
            </span>
          </div>

          <motion.div
            whileHover={{
              x: 4,
            }}
            className="flex items-center gap-2 text-[#c9973a]"
          >
            <span
              className="text-[0.72rem] uppercase tracking-[0.25em]"
              style={{
                fontFamily: "'Jost', sans-serif",
              }}
            >
              Premium
            </span>

            <FiArrowUpRight className="text-lg" />
          </motion.div>
        </div>
      </div>

      {/* Border hover */}
      <div className="absolute inset-0 border border-transparent transition-all duration-500 group-hover:border-[#c9973a]/20" />
    </motion.div>
  );
}

/* ───────────────── PAGE ───────────────── */

export default function ProductosPage() {
  return (
    <PageTransition>
      <section className="relative min-h-screen overflow-hidden bg-[#0f0d0b] text-white">
        {/* GRID */}
        <div
          className="
            absolute inset-0
            bg-[linear-gradient(rgba(201,151,58,0.04)_1px,transparent_1px),linear-gradient(90deg,rgba(201,151,58,0.04)_1px,transparent_1px)]
            bg-[size:60px_60px]
          "
        />

        {/* GLOW */}
        <motion.div
          animate={{
            scale: [1, 1.1, 1],
            opacity: [0.5, 1, 0.5],
          }}
          transition={{
            duration: 7,
            repeat: Infinity,
            ease: "easeInOut",
          }}
          className="
            absolute
            left-1/2
            top-1/2
            h-[45rem]
            w-[45rem]
            -translate-x-1/2
            -translate-y-1/2
            rounded-full
            bg-[radial-gradient(circle,rgba(201,151,58,0.12)_0%,transparent_70%)]
          "
        />

        {/* TOP GLOW */}
        <div className="absolute left-[-10rem] top-[-10rem] h-[30rem] w-[30rem] rounded-full bg-[#c9973a]/10 blur-[120px]" />

        {/* BOTTOM GLOW */}
        <div className="absolute bottom-[-12rem] right-[-10rem] h-[35rem] w-[35rem] rounded-full bg-[#8b3520]/20 blur-[140px]" />

        {/* CONTENT */}
        <div className="relative z-10 mx-auto max-w-7xl px-5 pb-24 pt-28 md:px-8 lg:pt-36">
          {/* HERO */}
          <motion.div
            initial={{
              opacity: 0,
              y: 40,
            }}
            animate={{
              opacity: 1,
              y: 0,
            }}
            transition={{
              duration: 1,
              ease: [0.22, 1, 0.36, 1],
            }}
            className="mx-auto max-w-5xl text-center"
          >
            {/* Eyebrow */}
            <div
              className="
                mb-6
                inline-flex
                items-center
                gap-3
                border
                border-[#c9973a]/20
                bg-[#c9973a]/5
                px-5
                py-2
              "
            >
              <div className="h-2 w-2 rounded-full bg-[#c9973a]" />

              <span
                className="
                  text-[0.68rem]
                  uppercase
                  tracking-[0.45em]
                  text-[#c9973a]
                "
                style={{
                  fontFamily: "'Jost', sans-serif",
                }}
              >
                Calidad · Diseño · Durabilidad
              </span>
            </div>

            {/* Title */}
            <h1
              className="
                text-[4rem]
                leading-[0.9]
                md:text-[7rem]
              "
              style={{
                fontFamily: "'Cormorant Garamond', serif",
                fontWeight: 300,
                color: "#f9f5ef",
              }}
            >
              Nuestros
              <br />

              <span
                className="
                  bg-gradient-to-r
                  from-[#f0c96b]
                  via-[#c9973a]
                  to-[#fde68a]
                  bg-clip-text
                  text-transparent
                "
                style={{
                  fontWeight: 700,
                }}
              >
                Productos
              </span>
            </h1>

            {/* Subtitle */}
            <p
              className="
                mx-auto
                mt-8
                max-w-3xl
                text-[1rem]
                leading-9
                text-[#b7aea2]
                md:text-[1.1rem]
              "
              style={{
                fontFamily: "'Jost', sans-serif",
                fontWeight: 300,
              }}
            >
              Fabricamos e instalamos aberturas de aluminio
              premium combinando tecnología, diseño y
              terminaciones de excelencia para transformar
              cada espacio.
            </p>
          </motion.div>

          {/* GRID */}
          <motion.div
            variants={container}
            initial="hidden"
            whileInView="show"
            viewport={{
              once: true,
              amount: 0.15,
            }}
            className="
              mt-20
              grid
              grid-cols-1
              gap-6
              md:grid-cols-2
              xl:grid-cols-3
            "
          >
            {PRODUCTS.map((product) => (
              <ProductCard
                key={product.num}
                product={product}
              />
            ))}
          </motion.div>

          {/* CTA */}
          <motion.div
            initial={{
              opacity: 0,
              y: 50,
            }}
            whileInView={{
              opacity: 1,
              y: 0,
            }}
            viewport={{
              once: true,
            }}
            transition={{
              duration: 0.9,
            }}
            className="
              relative
              mt-28
              overflow-hidden
              border
              border-[#c9973a]/10
              bg-white/[0.03]
              p-8
              backdrop-blur-xl
              md:p-14
            "
          >
            {/* Glow */}
            <div className="absolute inset-0 bg-gradient-to-r from-[#c9973a]/10 via-transparent to-[#8b3520]/20" />

            <div className="relative z-10 flex flex-col items-center justify-between gap-10 lg:flex-row">
              <div className="max-w-2xl">
                <span
                  className="
                    text-[0.72rem]
                    uppercase
                    tracking-[0.4em]
                    text-[#c9973a]
                  "
                  style={{
                    fontFamily: "'Jost', sans-serif",
                  }}
                >
                  Presupuesto sin cargo
                </span>

                <h2
                  className="
                    mt-5
                    text-[3rem]
                    leading-none
                    text-[#f9f5ef]
                    md:text-[4.5rem]
                  "
                  style={{
                    fontFamily: "'Cormorant Garamond', serif",
                    fontWeight: 400,
                  }}
                >
                  Transformamos ideas en espacios únicos
                </h2>

                <p
                  className="
                    mt-6
                    max-w-xl
                    text-[1rem]
                    leading-8
                    text-[#b7aea2]
                  "
                  style={{
                    fontFamily: "'Jost', sans-serif",
                    fontWeight: 300,
                  }}
                >
                  Te asesoramos en cada etapa para encontrar
                  la solución perfecta para tu hogar o
                  proyecto arquitectónico.
                </p>
              </div>

              {/* BUTTON */}
              <motion.a
                href="https://wa.me/542236901020?text=Hola!%20Quiero%20consultar%20sobre%20productos%20y%20precios."
                target="_blank"
                rel="noreferrer"
                whileHover={{
                  y: -3,
                  boxShadow:
                    "0 18px 50px rgba(201,151,58,0.25)",
                }}
                whileTap={{
                  scale: 0.97,
                }}
                className="group relative overflow-hidden"
              >
                {/* bg */}
                <div className="absolute inset-0 bg-gradient-to-r from-[#8b6220] via-[#c9973a] to-[#f0c96b]" />

                {/* shine */}
                <motion.div
                  animate={{
                    x: ["-120%", "220%"],
                  }}
                  transition={{
                    duration: 3,
                    repeat: Infinity,
                    ease: "linear",
                  }}
                  className="
                    absolute
                    inset-y-0
                    w-24
                    rotate-12
                    bg-white/20
                    blur-xl
                  "
                />

                <div
                  className="
                    relative
                    flex
                    items-center
                    gap-3
                    px-10
                    py-5
                    text-[#1a1814]
                  "
                >
                  <span
                    className="
                      text-[0.78rem]
                      font-semibold
                      uppercase
                      tracking-[0.28em]
                    "
                    style={{
                      fontFamily: "'Jost', sans-serif",
                    }}
                  >
                    Solicitar presupuesto
                  </span>

                  <FiArrowUpRight className="text-xl transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
                </div>
              </motion.a>
            </div>
          </motion.div>
        </div>
      </section>
    </PageTransition>
  );
}
